// ADR-0011 Fatia 2 Wave 3 — embrulho do DEK pras públicas do comitê no submit.
//
// NÃO-FATAL + NÃO-BLOQUEANTE por contrato. A denúncia é o que importa: se a busca
// de chaves falhar (rede/timeout), retornar [] (flag OFF em prod), ou o wrap de um
// membro falhar, a denúncia segue SEM esses wraps (cofre+protocolo cobrem). O
// denunciante NUNCA vê erro/trava por causa disto.
//
// O DEK NUNCA sai do browser em claro: wrapDekForMember devolve RSA-OAEP(dek) —
// só o wrap (cifrado) vai no POST. O conteúdo já é cifrado com AAD=caseId (no wizard).

import { wrapDekForMember } from '@aions/crypto';
import { bytesToBase64 } from '../utils/bytes';
import { apiUrl } from './api-base';

export interface MemberWrap {
  readonly memberId: string;
  readonly wrappedDek: string; // base64 de RSA-OAEP(dek) — cifrado, nunca o DEK cru
}

interface PublicKeyItem {
  memberId: string;
  publicKeyJwk: string;
}

/** Timeout curto pra a busca de chaves NUNCA atrasar o submit (um GET que pode pendurar). */
const PUBLIC_KEYS_TIMEOUT_MS = 4000;

/**
 * Busca as públicas dos membros e embrulha o DEK pra cada uma. SEMPRE resolve
 * (nunca lança); pior caso devolve [] e a denúncia entra sem wraps de membro —
 * exatamente o comportamento de hoje. Em prod (flag OFF) o endpoint devolve [].
 */
export async function fetchAndWrapForMembers(dekRaw: Uint8Array): Promise<MemberWrap[]> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), PUBLIC_KEYS_TIMEOUT_MS);
    let resp: Response;
    try {
      resp = await fetch(apiUrl('/api/denuncia/public-keys'), { signal: ctrl.signal });
    } finally {
      clearTimeout(timer);
    }
    if (!resp.ok) return [];
    const members = (await resp.json()) as unknown;
    if (!Array.isArray(members) || members.length === 0) return [];

    const out: MemberWrap[] = [];
    for (const m of members as PublicKeyItem[]) {
      try {
        if (typeof m?.memberId !== 'string' || typeof m?.publicKeyJwk !== 'string') continue;
        const wrapped = await wrapDekForMember(dekRaw, m.publicKeyJwk);
        out.push({ memberId: m.memberId, wrappedDek: bytesToBase64(wrapped) });
      } catch {
        // Wrap de um membro falhou (JWK ruim etc.) — pula esse, não derruba os outros.
      }
    }
    return out;
  } catch {
    // Rede/timeout/parse/abort → denúncia segue sem wraps de membro. NÃO-FATAL.
    return [];
  }
}
